import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import {
  ElectronicParticipation,
  GeneralMeetingVoteType,
  ReceivedPowerOfAttorney,
} from "./meetings/generalMeeting/generalMeetingDomain";
import { logDebug } from "./utils/log";
import { isProductionEnvironment, isTestEnvironment } from "./utils/environments";
import { IndividualVote } from "./dbot/common/commonGeneralMeetingDomain";
import {
  ElectronicGeneralMeeting,
  GeneralMeetingStatus,
  StatusName,
} from "./meetings/generalMeeting/electronicGeneralMeetingDomain";
import { User } from "./types/UserInfo";
import { extractFirstUUID } from "./utils/strings";
import { CompanyRoleIdWithName } from "./dbot/common/commonDomain";

/* eslint-disable  @typescript-eslint/no-explicit-any */
const logWebSocketError = (frame: any) => {
  const hasErrorMessage = frame.headers && "message" in frame.headers;
  logDebug("frame: ", frame);
  if (hasErrorMessage) {
    logDebug("Broker reported error: " + (hasErrorMessage ? frame.headers["message"] : ""));
  }
  if (frame.body) {
    logDebug("Additional details: " + frame.body);
  }
};

const webSocketUrl = isProductionEnvironment()
  ? "wss://app.dcompany.no"
  : isTestEnvironment()
    ? "wss://test.dcompany.no"
    : "ws://localhost:8081";

export class WebSocketStore {
  client: Client;
  activeSubscriptions: TopicSubscription[] = [];

  constructor(user: User | null) {
    const id = extractFirstUUID(window.location.pathname);
    logDebug("WebSocketStore created in context with id: " + id);
    const userId = user?.userId && user.userId > 0 ? `user-${user.userId}` : `anonymous-${id}`;
    this.client = new Client({
      brokerURL: `${webSocketUrl}/api/ws-broker`,
      onConnect: (frame) => {
        logDebug("Connected to broker: " + webSocketUrl);
        logDebug("username:", frame.headers["user-name"]);
        this.resubscribeToActiveSubscriptions();
      },
      reconnectDelay: 5000,
      connectHeaders: { id: userId },
      debug: function (str) {
        logDebug("::", str);
      },
    });
    this.client.activate();
    this.client.onWebSocketError = logWebSocketError;
    this.client.onStompError = logWebSocketError;
    this.client.onDisconnect = () => {
      logDebug("Disconnected from broker");
    };
  }

  unsubscribe(topic: TopicName) {
    const topicSubscription = this.activeSubscriptions.find((s) => s.topic === topic);
    if (topicSubscription) {
      topicSubscription.subscription.unsubscribe();
      this.activeSubscriptions = this.activeSubscriptions.filter((s) => s.topic !== topic);
    }
    if (!this.activeSubscriptions.length) {
      this.client.deactivate();
    }
  }

  resubscribeToActiveSubscriptions() {
    this.activeSubscriptions = this.activeSubscriptions.map((s) => {
      logDebug("resubscribing to topic: " + s.topic.split("/").pop());
      const subscription = this.client.subscribe(s.topic, (message) =>
        s.onMessage(this.decode(message)),
      );
      return { ...s, subscription };
    });
  }

  private decode(message: IMessage) {
    return JSON.parse(message.body);
  }

  subscribe(
    topic: TopicName,
    onMessage: (message: WebSocketMessage) => void,
    onSubscribed?: (subscription: SubscriptionContext) => void,
  ) {
    if (!this.client.active) {
      this.client.activate();
    }
    const onReadyForSubscribe = () => {
      if (!this.client.connected) {
        logDebug("Not connected to broker, cannot subscribe to topic");
      }
      logDebug("Subscribing to topic: " + topic.split("/").pop());
      const subscription = this.client.subscribe(topic, (message) =>
        onMessage(this.decode(message)),
      );
      this.activeSubscriptions.push({ topic, onMessage, subscription });
      const context: SubscriptionContext = {
        unsubscribe: () => {
          this.unsubscribe(topic);
        },
      };
      onSubscribed?.(context);
    };
    const maxAttempts = 1000;
    let attempts = 0;
    const subscribeIfConnected = () => {
      if (this.client.connected) {
        onReadyForSubscribe();
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(subscribeIfConnected, 7);
        }
      }
    };
    subscribeIfConnected();
  }
}

type TopicSubscription = {
  topic: TopicName;
  onMessage: (message: WebSocketMessage) => void;
  subscription: StompSubscription;
};

export type SubscriptionContext = {
  unsubscribe: () => void;
};

// union type for all websocket messages
export type WebSocketMessage = ShareholderGeneralMeetingMessage | AdminGeneralMeetingMessage;

// union type for all general meeting messages received by shareholder

export type ShareholderGeneralMeetingMessage =
  | AdminUpdatedMeetingStatus
  | AdminChangedMeetingChair
  | AdminMeetingAnnouncement
  | AdminVoted
  | ShareholderRegisteredAttendance
  | ShareholderRegisteredPowerOfAttorney
  | RevokedPoAForParticipant
  | ReceivedPoAForParticipant;

// union type for all general meeting messages received by admin
export type AdminGeneralMeetingMessage = ShareholderRegisteredAttendance | ShareholderVoted;

export type AdminUpdatedMeetingStatus = {
  type: "meeting-status-updated";
  projectId: string;
  orgNo: string;
  statusName: StatusName;
  status: GeneralMeetingStatus;
  decisionId?: string;
  meeting: ElectronicGeneralMeeting;
  meetingChair?: CompanyRoleIdWithName;
  coSigners?: CompanyRoleIdWithName[];
};

export type AdminChangedMeetingChair = {
  type: "meeting-chair-changed";
  projectId: string;
  orgNo: string;
  meetingChair?: CompanyRoleIdWithName;
  coSigners?: CompanyRoleIdWithName[];
};

export type AdminMeetingAnnouncement = {
  type: "meeting-announcement";
  projectId: string;
  orgNo: string;
  announcement: string;
};

export type ShareholderRegisteredPowerOfAttorney = {
  type: "shareholder-power-of-attorney";
  shareholderId: number;
  projectId: string;
};

export type ReceivedPoAForParticipant = {
  type: "received-poa-for-participant";
  projectId: string;
  name: string;
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
};

export type RevokedPoAForParticipant = {
  type: "revoked-poa-for-participant";
  projectId: string;
  revokerName: string;
  revokedNoticeId: string;
};

export type ShareholderRegisteredAttendance = {
  type: "shareholder-participation";
  shareholderId: number;
  projectId: string;
  participation?: ElectronicParticipation;
};

export type AdminVoted = {
  type: "admin-voted";
  projectId: string;
  orgNo: string;
  shareholderId: number;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
  participation: ElectronicParticipation;
};

export type ShareholderVoted = {
  type: "shareholder-voted";
  projectId: string;
  voteId: string;
  shareholderId: number;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
  vote?: IndividualVote;
};

export type TopicName =
  | `/topic/${string}/admin-live-general-meeting`
  | `/topic/${string}/shareholder-live-general-meeting`
  | `/user/${string}/shareholder-live-general-meeting`;

export type SubscribeToWebSocketMessagesEvent = {
  type: "subscribe-to-web-socket-messages";
  topic: TopicName;
  onMessage: (message: WebSocketMessage) => void;
  onSubscribed?: (subscription: SubscriptionContext) => void;
};

export type UnsubscribeFromWebSocketMessagesEvent = {
  type: "unsubscribe-from-web-socket-messages";
  topics: TopicName[];
};
